import React, { useState, useRef, useEffect, ChangeEvent, CSSProperties } from "react";
import VoiceOverAdApi from "../api-services/voice-over-ad-api.service";
import { VoiceOverAd } from "../interfaces/voice-over-ad.interface";

interface VoiceOverAdButtonProps {
  bottomOffset?: number; 
}

export default function VoiceOverAdButton({ 
  bottomOffset = 24 
}: VoiceOverAdButtonProps) {
  const [tracks, setTracks] = useState<VoiceOverAd[]>([]);
  const [currentTrackIndex, setCurrentTrackIndex] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [duration, setDuration] = useState<number>(0);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const currentTrack = tracks[currentTrackIndex];

  // FIX 1: Prevent memory leaks using a clean active flag tracker
  useEffect(() => {
    let isMounted = true;
    const api = new VoiceOverAdApi();
    setIsLoading(true);
    
    api.getTracks()
      .then((data) => {
        if (isMounted) {
          setTracks(data);
          setCurrentTrackIndex(0);
          setIsPlaying(false); 
        }
      })
      .catch((error) => console.error(`Failed tracks load:`, error))
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false; // Cancels state commits if component unmounts mid-request
    };
  }, []);

  // Handle responsive tracking
  useEffect(() => {
    const checkMobileView = () => setIsMobile(window.innerWidth < 768);
    checkMobileView();
    window.addEventListener("resize", checkMobileView);
    return () => window.removeEventListener("resize", checkMobileView);
  }, []);

  // FIX 2: Dynamic listener callbacks that don't need constant rebuild cycles
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const setAudioData = () => setDuration(audio.duration || 0);
    const setAudioTime = () => setCurrentTime(audio.currentTime || 0);
    
    const handleEnded = () => {
      setTracks((currentTracks) => {
        if (currentTracks.length > 0) {
          setCurrentTrackIndex((prev) => (prev + 1) % currentTracks.length);
          setCurrentTime(0);
        }
        return currentTracks;
      });
    };

    audio.addEventListener("loadedmetadata", setAudioData);
    audio.addEventListener("timeupdate", setAudioTime);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("loadedmetadata", setAudioData);
      audio.removeEventListener("timeupdate", setAudioTime);
      audio.removeEventListener("ended", handleEnded);
    };
  }, []); // Empty dependency array means events bind safely ONCE.

  // Playback state synchronization
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !currentTrack) return;

    if (isPlaying) {
      audio.play().catch((err) => {
        console.warn("Autoplay deferred:", err);
        setIsPlaying(false);
      });
    } else {
      audio.pause();
    }
  }, [isPlaying, currentTrackIndex, currentTrack]);

  const togglePlay = (e: React.MouseEvent): void => {
    e.stopPropagation();
    setIsPlaying((prev) => !prev);
  };

  const handleProgressChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const newTime = parseFloat(e.target.value);
    setCurrentTime(newTime);
    if (audioRef.current) audioRef.current.currentTime = newTime;
  };

  const formatTime = (time: number): string => {
    if (isNaN(time) || !isFinite(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return minutes + ":" + (seconds < 10 ? "0" : "") + seconds;
  };

  if (isLoading || tracks.length === 0 || !currentTrack) return null;

  const fillPercentage = (currentTime / (duration || 1)) * 100;

  const runtimeContainerStyle: CSSProperties = {
    ...styles.floatingContainer,
    bottom: isMobile 
      ? (isExpanded ? 0 : `${bottomOffset}px`) 
      : isExpanded 
        ? "24px" 
        : `${bottomOffset}px`, 
    
    transition: "all 0.2s ease-in-out",
    
    ...(isMobile 
      ? isExpanded 
        ? { left: 0, right: 0, width: "100%" } 
        : { right: "24px" } 
      : isExpanded
        ? { right: "96px" } 
        : { right: "24px" }  
    )
  };

  const runtimeCardStyle: CSSProperties = {
    ...styles.playerCard,
    ...(isMobile 
      ? { width: "100%", borderRadius: "16px 16px 0 0", padding: "12px 16px 24px" } 
      : { borderRadius: "24px" } 
    )
  };

  return (
    <div style={runtimeContainerStyle}>
      <audio ref={audioRef} src={currentTrack.audioUrl} preload="metadata" />

      {isExpanded ? (
        <div style={runtimeCardStyle}>
          <img 
            src="/iceholidays-assets/images/icons/audio_icon.png" 
            alt="GD Advertisement logo" 
            style={isMobile ? { ...styles.thumbnail, width: "46px", height: "46px" } : styles.thumbnail} 
          />
          
          <div style={styles.mainContent}>
            <div style={styles.titleRow}>
              <button 
                onClick={togglePlay} 
                style={isMobile ? { ...styles.playPauseBtn, width: "36px", height: "36px" } : styles.playPauseBtn} 
                aria-label={isPlaying ? "Pause" : "Play"}
              >
                {isPlaying ? (
                  <svg width="10" height="12" viewBox="0 0 10 12" fill="white">
                    <rect width="3" height="12" rx="1" /><rect x="6" width="3" height="12" rx="1" />
                  </svg>
                ) : (
                  <svg width="10" height="12" viewBox="0 0 10 12" fill="white" style={{ marginLeft: '2px' }}>
                    <path d="M0 0L10 6L0 12Z" />
                  </svg>
                )}
              </button>
              
              <span style={styles.titleText}>{currentTrack.title}</span>
            </div>
            
            <div style={styles.progressRow}>
              <span style={styles.timeLabel}>{formatTime(currentTime)}</span>
              <input 
                type="range" min="0" max={duration || 100} value={currentTime} onChange={handleProgressChange}
                style={{
                  ...styles.progressBar,
                  height: isMobile ? "6px" : "4px",
                  background: `linear-gradient(to right, #E52323 0%, #E52323 ${fillPercentage}%, #D3D3D3 ${fillPercentage}%, #D3D3D3 100%)`
                }}
              />
              <span style={styles.timeLabel}>{formatTime(duration)}</span>
            </div>
          </div>

          <button onClick={() => setIsExpanded(false)} style={isMobile ? { ...styles.closeBtn, minWidth: "36px", minHeight: "36px" } : styles.closeBtn} aria-label="Collapse Ad Player">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#5E6366" strokeWidth="2" strokeLinecap="round">
              <path d="M1 1L13 13M1 13L13 1" />
            </svg>
          </button>
        </div>
      ) : (
        <div style={styles.collapsedStack}>
          <div onClick={() => setIsExpanded(true)} style={styles.circularTrigger} title="Play Advertisement Audio">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#E52323" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2v20M17 5v14M22 9v6M7 7v10M2 10v4" />
            </svg>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  floatingContainer: { position: "fixed", zIndex: 9999, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', display: "flex", alignItems: "center" },
  collapsedStack: { display: "flex", flexDirection: "column", alignItems: "center", gap: "12px" },
  circularTrigger: { width: "54px", height: "54px", borderRadius: "50%", backgroundColor: "#ffffff", boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", border: "1px solid rgba(229, 35, 35, 0.15)" },
  playerCard: { display: "flex", alignItems: "center", backgroundColor: "#ffffff", padding: "16px 20px", boxShadow: "0px -4px 20px rgba(0, 0, 0, 0.06), 0px 10px 30px rgba(0, 0, 0, 0.08)", width: "440px", boxSizing: "border-box" },
  thumbnail: { width: "56px", height: "56px", borderRadius: "14px", objectFit: "cover" },
  mainContent: { flex: 1, marginLeft: "14px", marginRight: "14px", display: "flex", flexDirection: "column", justifyContent: "center", overflow: "hidden" },
  titleRow: { display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" },
  playPauseBtn: { background: "#FF9800", border: "none", borderRadius: "50%", width: "28px", height: "28px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", outline: "none", boxShadow: "0px 2px 5px rgba(255, 152, 0, 0.3)", flexShrink: 0 },
  titleText: { fontSize: "16px", fontWeight: "600", color: "#000000", letterSpacing: "-0.2px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flex: 1 },
  progressRow: { display: "flex", alignItems: "center", width: "100%" },
  timeLabel: { fontSize: "12px", color: "#5E6366", minWidth: "28px" },
  progressBar: { flex: 1, border: "none", borderRadius: "3px", margin: "0 8px", cursor: "pointer", WebkitAppearance: "none", appearance: "none", outline: "none" },
  closeBtn: { background: "none", border: "none", cursor: "pointer", padding: "8px", display: "flex", alignItems: "center", justifyContent: "center", alignSelf: "flex-start", marginTop: "2px", flexShrink: 0 }
};